import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-about-us.jpg";

const coreValues = [
  { icon: "favorite", title: "Love", description: "We foster an environment of love, care, and compassion for every child." },
  { icon: "verified", title: "Integrity", description: "We uphold honesty and strong moral principles in all we do." },
  { icon: "star", title: "Excellence", description: "We strive for the highest standards in academics and character." },
  { icon: "handshake", title: "Respect", description: "We teach mutual respect for self, others, and authority." },
  { icon: "volunteer_activism", title: "Service", description: "We encourage serving others and giving back to the community." },
  { icon: "menu_book", title: "Faith", description: "We ground our education in Christian values and biblical principles." },
];

function About() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/admissions",
          label: "Enroll Now",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              About Us
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Discover the story, vision, and mission of Rose of Sharon Primary School
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* School Background */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" mb={2}>
                  Our Story
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  Rose of Sharon Primary School was established with a divine calling to provide 
                  quality, Christ-centered education to children in our community. Our name is 
                  inspired by the biblical reference to Jesus Christ as the &quot;Rose of Sharon&quot; 
                  (Song of Solomon 2:1), symbolizing beauty, growth, and divine love.
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  From our humble beginnings, we have grown into a thriving educational institution 
                  known for academic excellence and strong moral foundation. We remain committed to 
                  our founding principles while embracing modern educational practices.
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Today, Rose of Sharon continues to be a beacon of hope and excellence, shaping 
                  young minds and preparing them for a bright future grounded in faith and knowledge.
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/about-students.jpg"
                  alt="School building"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 400 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Vision & Mission */}
        <MKBox component="section" py={6} sx={{ backgroundColor: "#f8f9fa" }}>
          <Container>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <MKBox
                  p={4}
                  borderRadius="xl"
                  sx={{
                    backgroundColor: "white",
                    boxShadow: ({ boxShadows: { md } }) => md,
                    height: "100%",
                  }}
                >
                  <MKBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    borderRadius="50%"
                    sx={{ backgroundColor: "#2e7d32", mb: 2 }}
                  >
                    <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>visibility</Icon>
                  </MKBox>
                  <MKTypography variant="h4" mb={2}>
                    Our Vision
                  </MKTypography>
                  <MKTypography variant="body1" color="text">
                    To be a leading educational institution that nurtures academically excellent, 
                    morally upright, and spiritually grounded individuals who will positively 
                    impact their communities and the world. We envision producing well-rounded 
                    graduates who embody Christian values and excel in all areas of life.
                  </MKTypography>
                </MKBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MKBox
                  p={4}
                  borderRadius="xl"
                  sx={{
                    backgroundColor: "white",
                    boxShadow: ({ boxShadows: { md } }) => md,
                    height: "100%",
                  }}
                >
                  <MKBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    borderRadius="50%"
                    sx={{ backgroundColor: "#006747", mb: 2 }}
                  >
                    <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>flag</Icon>
                  </MKBox>
                  <MKTypography variant="h4" mb={2}>
                    Our Mission
                  </MKTypography>
                  <MKTypography variant="body1" color="text">
                    To provide quality, holistic education in a safe, nurturing, and Christ-centered 
                    environment. We are committed to developing each child&apos;s intellectual, social, 
                    emotional, and spiritual potential through dedicated teaching, strong discipline, 
                    and the integration of Christian values in all aspects of learning.
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Core Values */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Our Core Values
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  The principles that guide everything we do at Rose of Sharon Primary School.
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {coreValues.map((value, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <MKBox textAlign="center" p={3}>
                    <MKBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      borderRadius="50%"
                      sx={{ backgroundColor: "#006747", margin: "0 auto 1rem" }}
                    >
                      <Icon sx={{ color: "#d32f2f", fontSize: "1.5rem" }}>{value.icon}</Icon>
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {value.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {value.description}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* Christian Foundation */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
          }}
        >
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MKTypography variant="h3" color="white" mb={2}>
                  Our Christian Foundation
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  At Rose of Sharon Primary School, our Christian faith is not just an add-on—it&apos;s 
                  the foundation of everything we do. We believe that true education must address 
                  the whole child: mind, body, and spirit.
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9} mb={2}>
                  Our students participate in regular devotions, chapel services, and Bible-based 
                  character education programs. We teach them to apply biblical principles to their 
                  daily lives and to develop a personal relationship with God.
                </MKTypography>
                <MKTypography variant="body1" color="white" opacity={0.9}>
                  We welcome families from all backgrounds who share our commitment to raising 
                  children with strong moral values and a heart for service.
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={6}>
                <MKBox
                  component="img"
                  src="/christian-foundation.jpg"
                  alt="Children praying"
                  borderRadius="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Discipline & Character */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={6} order={{ xs: 2, lg: 1 }}>
                <MKBox
                  component="img"
                  src="/discipline-character.jpg"
                  alt="Students in uniform"
                  borderRadius="lg"
                  shadow="lg"
                  width="100%"
                  sx={{ objectFit: "cover", height: { xs: 300, lg: 350 } }}
                />
              </Grid>
              <Grid item xs={12} lg={6} order={{ xs: 1, lg: 2 }}>
                <MKTypography variant="h3" mb={2}>
                  Discipline & Character Development
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  We believe that discipline is essential for success in life. At Rose of Sharon, 
                  we maintain high standards of behavior while creating a supportive environment 
                  where children learn self-control, responsibility, and respect.
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  Our character development program focuses on building qualities such as honesty, 
                  kindness, perseverance, and leadership. We use positive reinforcement and 
                  restorative practices to guide students toward making good choices.
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Our students wear their uniforms with pride, demonstrating equality, unity, 
                  and school spirit. They learn that discipline is not about punishment but 
                  about developing the self-control needed to achieve their dreams.
                </MKTypography>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default About;
